import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Login from './components/Login/Login';
import SsoCallback from './components/Login/SsoCallback';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import AppShell from './components/layout/AppShell';
import DashboardHome from './features/Dashboard/DashboardHome';
import AssetSetupPage from './features/Admin/AssetSetupPage';
import CloudSetupPage from './features/Admin/CloudSetupPage';
import CloudAssetsPage from './features/CloudAssets/CloudAssetsPage';
import InventorySetupPage from './features/Admin/InventorySetupPage';
import LicenseSetupPage from './features/Admin/LicenseSetupPage';
import UserAccessSetupPage from './features/Admin/UserAccessSetupPage';
import SystemSettings from './features/Admin/SystemSettings';
import NotificationSettings from './features/Admin/NotificationSettings';
import AssetManagement from './features/Assets/AssetManagement';
import AssetDetails from './features/Assets/AssetDetails';
import AssetAuditLog from './features/Assets/AssetAuditLog';
import LicenseManagement from './features/Licenses/LicenseManagement';
import AnalyticsDashboard from './features/Analytics/AnalyticsDashboard';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { CurrencyProvider } from './context/CurrencyContext';

/* ─── New Inventory-Management Pages ─── */
import CatalogPage from './features/Catalog/CatalogPage';
import StockPage from './features/Stock/StockPage';
import AssetsListPage from './features/AssetUnits/AssetsListPage';
import AssetDetailPage from './features/AssetUnits/AssetDetailPage';
import IssueReturnPage from './features/IssueReturn/IssueReturnPage';
import HoldingsPage from './features/Holdings/HoldingsPage';
import OverduePage from './features/Overdue/OverduePage';
import AuditPage from './features/Audit/AuditPage';
import AuditReportPage from './features/Audit/AuditReportPage';
import UserProfile from './features/UserManagement/UserProfile';
import UserSettings from './features/UserManagement/UserSettings';
import InventoryManagementModule from './features/ConsumableInventory/InventoryManagementModule';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { NotFoundPage } from './components/shared/NotFoundPage';

// Landing for /dashboard. Users with a dashboard scope (global/department) get
// the org dashboard; everyone else (Standard Users) is sent to My Portfolio.
function DashboardLanding() {
  const { hasAnyPermission } = useAuth();
  if (!hasAnyPermission(['dashboard.view.all', 'dashboard.view.department'])) {
    return <Navigate to="/dashboard/profile" replace />;
  }
  return <DashboardHome />;
}

// AppShell only hides the "Analytics" nav link for users without reports.view —
// a guessed URL would otherwise still render the page, since the underlying
// /api/dashboard/* data calls only require authentication. Guard the route too.
function AnalyticsRoute() {
  const { hasPermission } = useAuth();
  if (!hasPermission('reports.view')) {
    return <Navigate to="/dashboard" replace />;
  }
  return <AnalyticsDashboard />;
}

function App() {
  return (
    <ThemeProvider>
    <ToastProvider>
      <CurrencyProvider>
        <Router>
          <ErrorBoundary>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<SsoCallback />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              <Route path="/dashboard" element={<AppShell />}>
                <Route index element={<DashboardLanding />} />
                <Route path="admin/user-access-setup" element={<UserAccessSetupPage />} />
                <Route path="admin/users" element={<Navigate to="/dashboard/admin/user-access-setup?tab=users" replace />} />
                <Route path="admin/user-setup" element={<Navigate to="/dashboard/admin/user-access-setup?tab=departments" replace />} />
                <Route path="assets" element={<AssetManagement />} />
                <Route path="assets/:id" element={<AssetDetails />} />
                <Route path="assets/:id/audit" element={<AssetAuditLog />} />
                <Route path="licenses" element={<LicenseManagement />} />
                <Route path="cloud-assets" element={<CloudAssetsPage />} />
                <Route path="inventory" element={<InventoryManagementModule />} />
                <Route path="analytics" element={<AnalyticsRoute />} />
                <Route path="admin/access-control" element={<Navigate to="/dashboard/admin/user-access-setup?tab=roles" replace />} />
                <Route path="admin/asset-setup" element={<AssetSetupPage />} />
                <Route path="admin/cloud-setup" element={<CloudSetupPage />} />
                <Route path="admin/inventory-setup" element={<InventorySetupPage />} />
                <Route path="admin/license-setup" element={<LicenseSetupPage />} />
                <Route path="admin/inventory-categories" element={<Navigate to="/dashboard/admin/inventory-setup?tab=categories" replace />} />
                <Route path="admin/plans" element={<Navigate to="/dashboard/admin/license-setup?tab=plans" replace />} />
                <Route path="admin/settings" element={<SystemSettings />} />
                <Route path="admin/notifications" element={<NotificationSettings />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="settings" element={<UserSettings />} />

                {/* ─── New Inventory-Management Routes ─── */}
                <Route path="catalog" element={<CatalogPage />} />
                <Route path="stock" element={<StockPage />} />
                <Route path="asset-units" element={<AssetsListPage />} />
                <Route path="asset-units/:id" element={<AssetDetailPage />} />
                <Route path="issue-return" element={<IssueReturnPage />} />
                <Route path="holdings" element={<HoldingsPage />} />
                <Route path="employees/:id/holdings" element={<HoldingsPage />} />
                <Route path="overdue" element={<OverduePage />} />
                <Route path="audit" element={<AuditPage />} />
                <Route path="audit-report" element={<AuditReportPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              <Route path="/" element={<Login />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ErrorBoundary>
        </Router>
      </CurrencyProvider>
    </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
